import { CSP_VERSION, POSTHOG_CSP_REPORT_TOKEN, POSTHOG_REPORT_HOST } from "@/config/csp";
import { isPostHogDisabledPath } from "@/lib/posthogPathGuard";
import { getPostHogCorrelationIdsPostHog } from "@/lib/posthogAnalytics";

type CspViolationDetail = {
  documentURI?: string;
  violatedDirective?: string;
  effectiveDirective?: string;
  originalPolicy?: string;
  blockedURI?: string;
  disposition?: "enforce" | "report";
  lineNumber?: number;
  columnNumber?: number;
  sourceFile?: string;
  sample?: string;
  statusCode?: number;
  referrer?: string;
};

let listenerAttached = false;
const queuedViolations: Array<{ detail: CspViolationDetail; ts: string }> = [];

function getCurrentIds(): { session_id: string | null; distinct_id: string | null } {
  try {
    const ids = getPostHogCorrelationIdsPostHog();
    return {
      distinct_id: ids.distinct_id ?? null,
      session_id: ids.session_id ?? null,
    };
  } catch {
    return { session_id: null, distinct_id: null };
  }
}

function buildReportUrl(ids?: { session_id?: string | null; distinct_id?: string | null }): string {
  const { session_id, distinct_id } = ids ?? getCurrentIds();
  const params = new URLSearchParams({
    token: POSTHOG_CSP_REPORT_TOKEN,
    v: CSP_VERSION,
  });
  if (session_id) params.set("session_id", session_id);
  if (distinct_id) params.set("distinct_id", distinct_id);
  return `${POSTHOG_REPORT_HOST}/report/?${params.toString()}`;
}

/**
 * Send a CSP violation report to PostHog.
 * Prefers navigator.sendBeacon for reliability during page teardown.
 */
function sendCspReport(detail: CspViolationDetail) {
  if (typeof window === "undefined") return;

  // Avoid sending from admin or explicitly disabled paths (defense in depth)
  try {
    if (isPostHogDisabledPath(window.location.pathname)) {
      return;
    }
  } catch {
    // ignore
  }

  const ids = getCurrentIds();
  const url = buildReportUrl(ids);

  // Build a payload compatible with PostHog CSP ingestion.
  // We send a JSON object; PostHog accepts both the legacy csp-report wrapper
  // and flattened details for the /report/ endpoint.
  const payload = {
    type: "csp-violation",
    version: CSP_VERSION,
    document_uri: detail.documentURI,
    violated_directive: detail.violatedDirective,
    effective_directive: detail.effectiveDirective,
    original_policy: detail.originalPolicy,
    blocked_uri: detail.blockedURI,
    disposition: detail.disposition,
    line_number: detail.lineNumber,
    column_number: detail.columnNumber,
    source_file: detail.sourceFile,
    sample: detail.sample,
    status_code: detail.statusCode,
    referrer: detail.referrer,
    // Also include the raw detail for debugging
    raw: detail,
    // Include ids explicitly in body as well (helps if query params are stripped)
    ph_session_id: ids.session_id,
    ph_distinct_id: ids.distinct_id,
    reported_at: new Date().toISOString(),
  };

  const body = JSON.stringify(payload);

  // Also emit via the PostHog JS SDK when available.
  // This makes CSP violations first-class events in the product analytics
  // (trends, funnels, error tracking, session-linked) — aligned with how
  // trackErrorPostHog, funnel failures, etc. work in this codebase.
  // The direct beacon to /report/ (with ?v &session_id &distinct_id) remains
  // the primary mechanism for PostHog's dedicated CSP report ingestion + replay linking.
  try {
    const ph = (window as any).posthog;
    if (ph && typeof ph.capture === "function" && ph.__loaded) {
      ph.capture("csp_violation", {
        ...payload,
        csp_version: CSP_VERSION,
      });
    }
  } catch {
    /* the /report/ beacon below is the guaranteed path */
  }

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      // sendBeacon returns boolean; we don't await it
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    // fall through to fetch
  }

  // Fallback: fetch with keepalive
  try {
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      // credentials not needed for this public ingestion endpoint
    }).catch(() => {
      /* swallow network errors for reporting */
    });
  } catch {
    /* noop */
  }
}

function handleSecurityPolicyViolation(event: SecurityPolicyViolationEvent) {
  const detail: CspViolationDetail = {
    documentURI: event.documentURI,
    violatedDirective: event.violatedDirective,
    effectiveDirective: event.effectiveDirective,
    originalPolicy: event.originalPolicy,
    blockedURI: event.blockedURI,
    disposition: event.disposition as "enforce" | "report" | undefined,
    lineNumber: event.lineNumber,
    columnNumber: event.columnNumber,
    sourceFile: event.sourceFile,
    sample: event.sample,
    statusCode: event.statusCode,
    referrer: (event as any).referrer,
  };

  // If PostHog not ready yet (no session/distinct ids), queue a few and flush later
  // when the 'ph:posthog_ready' event fires (see main.tsx loaded + flushQueued).
  // We use the shared helper so the readiness check is the same one used by
  // all other features that need to link to replay/session (funnels, alerts, etc).
  const idsReady = getPostHogCorrelationIdsPostHog();
  const hasIds = !!(idsReady.session_id || idsReady.distinct_id);
  if (!hasIds && queuedViolations.length < 5) {
    queuedViolations.push({ detail, ts: new Date().toISOString() });
  }

  sendCspReport(detail);
}

function flushQueued() {
  if (!queuedViolations.length) return;
  const toSend = queuedViolations.splice(0, queuedViolations.length);
  for (const item of toSend) {
    sendCspReport(item.detail);
  }
}

/**
 * Install the global securitypolicyviolation listener and wire PostHog ids.
 * Safe to call multiple times; attaches at most once.
 */
export function setupCspViolationReporting(): void {
  if (typeof window === "undefined" || listenerAttached) return;

  // Attach early
  try {
    document.addEventListener("securitypolicyviolation", handleSecurityPolicyViolation, true);
    listenerAttached = true;
  } catch {
    // ignore
  }

  // When PostHog becomes available, flush anything we queued and re-send one
  // synthetic "ids now available" ping if desired (no-op for now).
  const tryFlush = () => {
    try {
      const ph = (window as any).posthog;
      if (ph && typeof ph.get_session_id === "function") {
        flushQueued();
        // Optional: remove listener if we want to re-attach after ids ready (not needed)
      }
    } catch {
      /* noop */
    }
  };

  // Poll a few times shortly after load in case PostHog finishes async
  const schedule = (ms: number) => setTimeout(tryFlush, ms);
  schedule(1500);
  schedule(4000);
  schedule(9000);

  // Also listen for the posthog_initialized event we fire in main.tsx
  try {
    window.addEventListener("ph:posthog_ready", tryFlush as EventListener, { once: true });
  } catch {
    /* noop */
  }
}

/** Build the canonical report URL (with current ids when possible). Useful for debugging. */
export function getCspReportUrlForDebug(): string {
  return buildReportUrl();
}

/** Manually report a CSP-like violation (testing / instrumentation). */
export function reportCspViolationManually(detail: Partial<CspViolationDetail>): void {
  sendCspReport(detail as CspViolationDetail);
}
